import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { List, ListOrdered, AlignLeft, AlignRight, Undo2, Redo2, Link as LinkIcon, Unlink, Check, X, Maximize2, Minimize2 } from 'lucide-react';
import { Tooltip } from './ui';

// Toolbar button component — title se renderiza como Radix Tooltip
const ToolbarButton = ({ onClick, isActive, children, title }) => (
    <Tooltip content={title}>
        <button
            type="button"
            className={`tiptap-btn ${isActive ? 'active' : ''}`}
            onClick={onClick}
            onMouseDown={(e) => e.preventDefault()}
            aria-label={typeof title === 'string' ? title : undefined}
        >
            {children}
        </button>
    </Tooltip>
);

// Link popup component
const LinkPopup = ({ editor, onClose }) => {
    const currentUrl = editor.getAttributes('link').href || '';
    const [url, setUrl] = useState(currentUrl || 'https://');
    const inputRef = useRef(null);

    useEffect(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
    }, []);

    const handleApply = () => {
        const trimmed = url.trim();
        if (trimmed && trimmed !== 'https://') {
            editor.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run();
        }
        onClose();
    };

    const handleRemove = () => {
        editor.chain().focus().unsetLink().run();
        onClose();
    };

    return (
        <div className="tiptap-link-popup">
            <input
                ref={inputRef}
                className="tiptap-link-input"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') handleApply();
                    if (e.key === 'Escape') onClose();
                }}
                placeholder="https://ejemplo.com"
            />
            <Tooltip content="Aplicar">
                <button type="button" className="tiptap-link-btn apply" onClick={handleApply} aria-label="Aplicar">
                    <Check size={14} />
                </button>
            </Tooltip>
            {currentUrl && (
                <Tooltip content="Quitar enlace">
                    <button type="button" className="tiptap-link-btn remove" onClick={handleRemove} aria-label="Quitar enlace">
                        <Unlink size={14} />
                    </button>
                </Tooltip>
            )}
            <Tooltip content="Cancelar">
                <button type="button" className="tiptap-link-btn cancel" onClick={onClose} aria-label="Cancelar">
                    <X size={14} />
                </button>
            </Tooltip>
        </div>
    );
};

// Main TipTap Editor component
const TipTapEditor = ({ value, onChange, placeholder = 'Escribe aquí...' }) => {
    const [showLinkPopup, setShowLinkPopup] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [, forceUpdate] = useState(0);

    // Esc cierra fullscreen, bloquea scroll del body al expandir
    useEffect(() => {
        if (!isFullscreen) return;
        const onKey = (e) => {
            if (e.key === 'Escape') setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
    }, [isFullscreen]);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3]
                },
                // En TipTap 3.x StarterKit ya incluye link y underline.
                // Los desactivamos aqui para usar las versiones standalone
                // (necesarias para configurar openOnClick: false del Link).
                link: false,
                underline: false
            }),
            Underline,
            TextAlign.configure({
                types: ['heading', 'paragraph']
            }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                }
            }),
            Placeholder.configure({
                placeholder
            })
        ],
        content: value || '',
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
        onSelectionUpdate: () => {
            forceUpdate(n => n + 1);
        }
    });

    // Sync external value changes
    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value || '');
        }
    }, [value, editor]);

    if (!editor) {
        return null;
    }

    // Envolvemos toda la UI del editor. Cuando esta en fullscreen, la mandamos
    // a un React Portal en <body> para escapar de ancestros con transform/filter
    // que rompen position:fixed y hacen que el modal no cubra todo el viewport.
    const editorUI = (
        <>
        {isFullscreen && (
            <div className="tiptap-fs-backdrop" onClick={() => setIsFullscreen(false)} />
        )}
        <div className={`tiptap-editor ${isFullscreen ? 'tiptap-fullscreen' : ''}`}>
            <div className="tiptap-toolbar">
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    isActive={editor.isActive('bold')}
                    title="Negrita"
                >
                    <strong>B</strong>
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    isActive={editor.isActive('italic')}
                    title="Cursiva"
                >
                    <em>I</em>
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    isActive={editor.isActive('underline')}
                    title="Subrayado"
                >
                    <u>U</u>
                </ToolbarButton>

                <span className="tiptap-divider" />

                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    isActive={editor.isActive('bulletList')}
                    title="Lista con viñetas"
                >
                    <List size={16} />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    isActive={editor.isActive('orderedList')}
                    title="Lista numerada"
                >
                    <ListOrdered size={16} />
                </ToolbarButton>

                <span className="tiptap-divider" />

                <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                    isActive={editor.isActive({ textAlign: 'left' })}
                    title="Alinear izquierda"
                >
                    <AlignLeft size={16} />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                    isActive={editor.isActive({ textAlign: 'right' })}
                    title="Alinear derecha"
                >
                    <AlignRight size={16} />
                </ToolbarButton>

                <span className="tiptap-divider" />

                <ToolbarButton
                    onClick={() => setShowLinkPopup(!showLinkPopup)}
                    isActive={editor.isActive('link')}
                    title={editor.isActive('link') ? 'Editar enlace' : 'Agregar enlace'}
                >
                    <LinkIcon size={16} />
                </ToolbarButton>

                <span className="tiptap-divider" />

                <ToolbarButton
                    onClick={() => editor.chain().focus().undo().run()}
                    isActive={false}
                    title="Deshacer"
                >
                    <Undo2 size={16} />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().redo().run()}
                    isActive={false}
                    title="Rehacer"
                >
                    <Redo2 size={16} />
                </ToolbarButton>

                <span className="tiptap-toolbar-spacer" />

                <ToolbarButton
                    onClick={() => setIsFullscreen(v => !v)}
                    isActive={isFullscreen}
                    title={isFullscreen ? 'Salir de pantalla completa (Esc)' : 'Expandir a pantalla completa'}
                >
                    {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </ToolbarButton>
            </div>
            {showLinkPopup && (
                <LinkPopup editor={editor} onClose={() => setShowLinkPopup(false)} />
            )}
            <EditorContent editor={editor} className="tiptap-content" />
        </div>
        </>
    );

    return isFullscreen ? createPortal(editorUI, document.body) : editorUI;
};

export default TipTapEditor;
